/**
 * Welcome page explorers for the Vyom V2 plan (3 Nov 2026).
 *
 * WorkflowExplorer: the story-first flow. The visitor writes the story, Vyom
 * writes the script and suggests a character for each important role, the
 * visitor approves the script and the cast, Vyom generates the production frame
 * by frame, and the visitor downloads the finished film after final approval.
 * EngineMap: the six layers that one cinematic engine decides and generates.
 * ScrollProgress: a thin page-scroll bar (decorative).
 *
 * Tabs follow the WAI-ARIA tabs pattern: roving tabindex, arrow keys with
 * wrap-around, Home and End. Motion is skipped under reduced motion.
 */
import { useRef, useState } from "react";
import { motion, useScroll } from "framer-motion";

export const WORKFLOW_STEPS = [
  {
    key: "story",
    label: "Story",
    actor: "You",
    title: "You write the story in plain words.",
    body: "Describe the film, ad, reel or short you want. One idea is enough for Vyom to begin.",
    checkpoint: "Nothing is generated yet.",
  },
  {
    key: "script",
    label: "Script",
    actor: "Vyom",
    title: "Vyom writes the complete script.",
    body: "A logical script with many touches: scenes, shots, dialogue, camera direction and music cues, written as one story for the whole production.",
    checkpoint: "The full script comes to you before anything is generated.",
  },
  {
    key: "characters",
    label: "Characters",
    actor: "Vyom",
    title: "Vyom suggests a character for each important role.",
    body: "Each suggested character has a face, wardrobe and voice that stay the same in every scene. You can ask for changes before you approve.",
    checkpoint: "The cast is suggested to you. Nothing is generated yet.",
  },
  {
    key: "approve",
    label: "Approve",
    actor: "You",
    title: "You approve the script and the cast.",
    body: "Read the full script, look over the cast, ask for changes, and approve both when they are right. Vyom does not start generating until you do.",
    checkpoint: "Approval is your decision, not an automatic step.",
  },
  {
    key: "generate",
    label: "Generate",
    actor: "Vyom",
    title: "Vyom generates the production frame by frame.",
    body: "The engine keeps every character consistent from scene to scene, with clean visuals, a steady flow, and music, sound effects and speech built in.",
    checkpoint: "Nothing can be downloaded yet. The finished film waits for your final approval.",
  },
  {
    key: "download",
    label: "Download",
    actor: "You",
    title: "You watch the finished film, then download it.",
    body: "The download opens only after you approve the finished film.",
    checkpoint: "Nothing leaves Vyom until you give final approval.",
  },
];

function useRovingTabs(count) {
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  const select = (index) => {
    setActive(index);
    const el = refs.current[index];
    if (el && typeof el.focus === "function") el.focus();
  };

  const onKeyDown = (event) => {
    const last = count - 1;
    let next = null;
    if (event.key === "ArrowRight") next = active === last ? 0 : active + 1;
    else if (event.key === "ArrowLeft") next = active === 0 ? last : active - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next !== null) {
      event.preventDefault();
      select(next);
    }
  };

  return { active, select, refs, onKeyDown };
}

const TAB_BASE = "shrink-0 snap-start text-left rounded-xl px-4 py-3 ring-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8c7cff]";
const TAB_ON = "bg-[#5B4DFF]/15 ring-[#5B4DFF]/60 text-white";
const TAB_OFF = "bg-white/[0.03] ring-white/10 text-slate-400 hover:text-white hover:ring-white/25";

export function WorkflowExplorer({ prm = false }) {
  const { active, select, refs, onKeyDown } = useRovingTabs(WORKFLOW_STEPS.length);
  const step = WORKFLOW_STEPS[active];
  const actorTone = step.actor === "You"
    ? "bg-[#5B4DFF]/20 text-[#c9c1ff] border-[#5B4DFF]/40"
    : "bg-[#FFB300]/15 text-[#ffd166] border-[#FFB300]/35";

  return (
    <div data-testid="v2-workflow">
      <div
        role="tablist"
        aria-label="Production steps"
        onKeyDown={onKeyDown}
        className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory sm:grid sm:grid-cols-3 lg:grid-cols-6 sm:overflow-visible"
      >
        {WORKFLOW_STEPS.map((s, i) => {
          const on = i === active;
          return (
            <button
              key={s.key}
              ref={(el) => { refs.current[i] = el; }}
              type="button"
              role="tab"
              id={`v2-wf-tab-${i}`}
              aria-selected={on}
              aria-controls="v2-wf-panel"
              tabIndex={on ? 0 : -1}
              onClick={() => select(i)}
              className={TAB_BASE + " " + (on ? TAB_ON : TAB_OFF)}
            >
              <span className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#8c7cff]">Step {i + 1}</span>
              <span className="block text-sm font-semibold mt-1">{s.label}</span>
            </button>
          );
        })}
      </div>

      <div className="relative h-1 mt-4 rounded-full bg-white/8 overflow-hidden" aria-hidden="true">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#5B4DFF] to-[#8c7cff]"
          initial={false}
          animate={{ width: `${((active + 1) / WORKFLOW_STEPS.length) * 100}%` }}
          transition={prm ? { duration: 0 } : { duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
        />
      </div>

      <motion.div
        key={step.key}
        role="tabpanel"
        id="v2-wf-panel"
        aria-labelledby={`v2-wf-tab-${active}`}
        initial={prm ? false : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={prm ? { duration: 0 } : { duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
        className="mt-8 grid lg:grid-cols-12 gap-6 rounded-2xl p-7 sm:p-9 bg-white/[0.03] ring-1 ring-white/10"
      >
        <div className="lg:col-span-7">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#8c7cff] mb-3">Step {active + 1} of {WORKFLOW_STEPS.length}</div>
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">{step.title}</h3>
          <p className="text-sm sm:text-base text-slate-300/90 mt-4 leading-relaxed max-w-xl">{step.body}</p>
        </div>
        <div className="lg:col-span-5 rounded-xl p-5 bg-black/30 ring-1 ring-white/10 self-start">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Who acts</div>
          <span data-testid="v2-workflow-actor" className={"inline-block mt-2 text-xs font-semibold px-2.5 py-1 rounded-md border " + actorTone}>{step.actor}</span>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mt-5">Checkpoint</div>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">{step.checkpoint}</p>
        </div>
      </motion.div>
    </div>
  );
}

export const ENGINE_LAYERS = [
  {
    key: "scenes",
    title: "Scenes",
    body: "From your approved script, the engine decides each scene: where it happens, what is in it and the order it plays in.",
  },
  {
    key: "people",
    title: "People and characters",
    body: "The engine casts the people and characters, then keeps their faces, wardrobe and presence the same in every scene.",
  },
  {
    key: "voice",
    title: "Voice and speech",
    body: "Every spoken line is generated by the engine, in a voice chosen for each character.",
  },
  {
    key: "music",
    title: "Music and sound",
    body: "The engine writes the music, sound effects and ambient audio for each scene and times them to the picture.",
  },
  {
    key: "camera",
    title: "Camera and picture",
    body: "The engine plans the camera moves and generates every frame of every shot, checked for clean, precise visuals.",
  },
  {
    key: "cut",
    title: "The final cut",
    body: "The engine joins its scenes, voices, music and picture into one complete film. You download it only after your final approval.",
  },
];

export function EngineMap() {
  return (
    <div data-testid="v2-engine">
      <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 list-none p-0 m-0">
        {ENGINE_LAYERS.map((layer, i) => (
          <li
            key={layer.key}
            className="rounded-2xl p-6 bg-white/[0.03] ring-1 ring-white/10 transition hover:ring-[#5B4DFF]/50"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#8c7cff]">{String(i + 1).padStart(2, "0")}</div>
            <h3 className="font-display text-xl font-semibold text-white mt-3">{layer.title}</h3>
            <p className="text-sm text-slate-300/90 mt-3 leading-relaxed">{layer.body}</p>
          </li>
        ))}
      </ol>
      <p className="text-xs sm:text-sm text-slate-500 mt-6 leading-relaxed max-w-3xl">
        Every layer is generated by the Vyom engine, from the same approved script.
      </p>
    </div>
  );
}

export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      data-testid="scroll-progress"
      aria-hidden="true"
      className="fixed top-0 inset-x-0 h-[2px] z-[60] origin-left pointer-events-none bg-gradient-to-r from-[#5B4DFF] via-[#8c7cff] to-[#FFB300]"
      style={{ scaleX: scrollYProgress }}
    />
  );
}
